import {
  HHBox,
  HHCard,
  HHCardContent,
  HHChip,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  ArrowForward,
  CheckCircle,
  CloudUpload,
  Warning,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Provider } from '../hooks/use-providers';

type ProviderCardProps = {
  provider: Provider;
  onClick: (source: string) => void;
};

export const ProviderCard = ({
  provider,
  onClick,
}: ProviderCardProps): JSX.Element => {
  const handleClick = (): void => {
    onClick(provider.source);
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return 'Unknown';
    }
  };

  const getProviderColor = (): string => {
    if (provider.status === 'active') {
      return provider.hasPublishedRules ? '#2E7D32' : '#F57C00';
    }
    return '#757575';
  };

  const getProviderColorSet = (): {
    primary: string;
    background: string;
    border: string;
    gradient: string;
  } => {
    if (provider.status === 'active') {
      if (provider.hasPublishedRules) {
        return {
          primary: '#2E7D32',
          background: '#F1F8E9',
          border: '#4CAF50',
          gradient: 'linear-gradient(135deg, #F1F8E9 0%, #E8F5E8 100%)',
        };
      } else {
        return {
          primary: '#F57C00',
          background: '#FFF8E1',
          border: '#FF9800',
          gradient: 'linear-gradient(135deg, #FFF8E1 0%, #FFF3C4 100%)',
        };
      }
    }
    return {
      primary: '#757575',
      background: '#FAFAFA',
      border: '#BDBDBD',
      gradient: 'linear-gradient(135deg, #FAFAFA 0%, #F5F5F5 100%)',
    };
  };

  const getStatusIcon = (): React.ElementType => {
    if (provider.status === 'active') {
      return provider.hasPublishedRules ? CheckCircle : Warning;
    }
    return CloudUpload;
  };

  const StatusIcon = getStatusIcon();
  const providerColor = getProviderColor();
  const colorSet = getProviderColorSet();

  return (
    <HHCard
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        height: '100%',
        minHeight: 180,
        border: `1px solid ${colorSet.border}`,
        borderRadius: '16px',
        position: 'relative',
        overflow: 'hidden',
        background: colorSet.gradient,
        backdropFilter: 'blur(8px)',
        '&:hover': {
          boxShadow: `0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px ${colorSet.border}`,
          transform: 'translateY(-6px) scale(1.02)',
          background: `linear-gradient(135deg, ${colorSet.background} 0%, rgba(255, 255, 255, 0.9) 100%)`,
          '& .provider-arrow': {
            transform: 'translateX(6px)',
            opacity: 1,
            color: colorSet.primary,
          },
          '& .provider-icon': {
            transform: 'scale(1.15)',
            boxShadow: `0 4px 12px ${colorSet.primary}40`,
          },
          '& .provider-card-content': {
            transform: 'translateY(-2px)',
          },
        },
        '&:focus-visible': {
          outline: `3px solid ${colorSet.primary}`,
          outlineOffset: '2px',
        },
        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
      tabIndex={0}
      role="button"
      onKeyDown={(e): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <HHCardContent
        className="provider-card-content"
        sx={{
          padding: '24px',
          height: '100%',
          transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <HHStack direction="column" spacing={3} sx={{ height: '100%' }}>
          <HHStack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <HHBox
              sx={{
                backgroundColor: `${colorSet.primary}20`,
                borderRadius: '14px',
                padding: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${colorSet.primary}30`,
                backdropFilter: 'blur(4px)',
              }}
            >
              <StatusIcon
                className="provider-icon"
                sx={{
                  fontSize: '24px',
                  color: providerColor,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            </HHBox>
            <ArrowForward
              className="provider-arrow"
              sx={{
                fontSize: '18px',
                color: 'text.secondary',
                opacity: 0.6,
                transform: 'translateX(0px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </HHStack>

          <HHStack direction="column" spacing={1} sx={{ flexGrow: 1 }}>
            <HHStack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <HHTypography
                hhVariant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: 1.3,
                  color: 'text.primary',
                  flex: 1,
                  marginRight: 1,
                }}
                noWrap
                title={provider.source}
              >
                {provider.source}
              </HHTypography>
              <HHChip
                label={provider.status === 'active' ? 'Active' : 'Inactive'}
                color={provider.status === 'active' ? 'success' : 'default'}
                size="small"
                hhVariant="filled"
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  borderRadius: '6px',
                }}
              />
            </HHStack>

            <HHBox sx={{ marginTop: '8px' }}>
              <HHTypography
                hhVariant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                {provider.rulesCount} config
                {provider.rulesCount !== 1 ? 's' : ''}
              </HHTypography>
              <HHTypography
                hhVariant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '12px',
                  marginTop: '2px',
                }}
              >
                Updated: {formatDate(provider.lastUpdated)}
              </HHTypography>
            </HHBox>

            {!provider.hasPublishedRules && provider.status === 'active' && (
              <HHBox
                sx={{
                  backgroundColor: colorSet.background,
                  border: `1px solid ${colorSet.border}`,
                  borderRadius: '10px',
                  padding: '10px 14px',
                  marginTop: 'auto',
                  backdropFilter: 'blur(4px)',
                  boxShadow: `0 2px 8px ${colorSet.primary}20`,
                }}
              >
                <HHTypography
                  hhVariant="caption"
                  sx={{
                    color: colorSet.primary,
                    fontSize: '11px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Warning sx={{ fontSize: '14px', color: colorSet.primary }} />
                  No published rules
                </HHTypography>
              </HHBox>
            )}
          </HHStack>
        </HHStack>
      </HHCardContent>
    </HHCard>
  );
};
